/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";
import { clearSessionToken, setSessionToken } from "@/utils/sessionToken";

interface User {
  id: number;
  username: string;
  totpEnabled?: boolean;
}

interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

interface RequiresTotpResponse {
  requiresTotp: true;
  pendingToken: string;
}

export type LoginResult = LoginResponse | RequiresTotpResponse;

export function loginRequiresTotp(result: LoginResult): result is RequiresTotpResponse {
  return "requiresTotp" in result && result.requiresTotp === true;
}

interface UserResponse {
  user: User | null;
}

interface RegistrationStatus {
  hasUsers: boolean;
  oidcConfigured: boolean;
  oidcReady: boolean;
}

export interface LoginAttempt {
  id: number;
  identifier: string;
  kind: string;
  ip: string;
  userAgent: string;
  success: boolean;
  method: string;
  createdAt: string;
}

export async function login(
  username: string,
  password: string
): Promise<LoginResult> {
  try {
    const response = await fetch(getApiUrl("/auth/login"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || "Login failed");
    }

    const data = await response.json();
    if (!loginRequiresTotp(data) && data.access_token) {
      setSessionToken(data.access_token);
    }
    return data;
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
}

export async function verifyTotp(
  pendingToken: string,
  code: string
): Promise<LoginResponse> {
  const response = await fetch(getApiUrl("/auth/login/totp"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ pendingToken, code }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Invalid code");
  }

  const data = await response.json();
  if (data.access_token) {
    setSessionToken(data.access_token);
  }
  return data;
}

export async function setupTotp(): Promise<{ otpauthUrl: string; qrCode: string; secret: string }> {
  const response = await fetch(getApiUrl("/auth/totp/setup"), {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to start 2FA setup");
  }
  return response.json();
}

export async function confirmTotp(code: string): Promise<{ backupCodes: string[] }> {
  const response = await fetch(getApiUrl("/auth/totp/confirm"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ code }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Invalid code");
  }
  return response.json();
}

export async function disableTotp(password: string): Promise<void> {
  const response = await fetch(getApiUrl("/auth/totp/disable"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ password }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to disable 2FA");
  }
}

export async function getLoginHistory(): Promise<LoginAttempt[]> {
  const response = await fetch(getApiUrl("/auth/login-history"), {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch login history");
  }
  return (await response.json()) ?? [];
}

export async function register(
  username: string,
  password: string
): Promise<LoginResponse> {
  try {
    const response = await fetch(getApiUrl("/auth/register"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || "Registration failed");
    }

    const data: LoginResponse = await response.json();
    if (data.access_token) {
      setSessionToken(data.access_token);
    }
    return data;
  } catch (error) {
    console.error("Register error:", error);
    throw error;
  }
}

export async function logout(): Promise<void> {
  try {
    const response = await fetch(getApiUrl("/auth/logout"), {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || "Registration failed");
    }
    clearSessionToken();
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
}

export async function verify(): Promise<boolean> {
  try {
    const response = await fetch(getApiUrl("/auth/verify"), {
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 401) {
        return false;
      }
      throw new Error("Session verification failed");
    }

    return true;
  } catch (error) {
    console.error("Session verification error:", error);
    return false;
  }
}

export async function getUserInfo(): Promise<UserResponse> {
  try {
    const response = await fetch(getApiUrl("/auth/user"), {
      credentials: "include",
    });

    if (!response.ok) {
      return { user: null };
    }

    const data: UserResponse = await response.json();
    return data;
  } catch (error) {
    console.error("User info retrieval error:", error);
    return { user: null };
  }
}

export async function updateAccount(
  username: string,
  currentPassword: string,
): Promise<UserResponse> {
  const response = await fetch(getApiUrl("/auth/account"), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ username, currentPassword }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to update account");
  }
  return response.json();
}

export async function updatePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const response = await fetch(getApiUrl("/auth/password"), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to update password");
  }
}

export async function checkRegistrationStatus(): Promise<RegistrationStatus> {
  try {
    const response = await fetch(getApiUrl("/auth/status"), {
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();
      return {
        hasUsers: data.hasUsers ?? true,
        oidcConfigured: data.oidcConfigured ?? false,
        oidcReady: data.oidcReady ?? false,
      };
    }

    // If the endpoint fails, assume users exist to prevent unwanted registration
    return {
      hasUsers: true,
      oidcConfigured: false,
      oidcReady: false,
    };
  } catch (error) {
    console.error("Registration status check failed:", error);
    // Default to secure state
    return {
      hasUsers: true,
      oidcConfigured: false,
      oidcReady: false,
    };
  }
}

export function getOIDCLoginUrl(): string {
  return getApiUrl("/auth/oidc/login");
}
